import {errorToString, useMachine} from "@cdx/common";
import {useState, useRef, useEffect, useCallback} from "react";
import type {MachineDescription} from "@cdx/common/hooks/useMachine";
import cdxEnv from "../env";
import type {ReleaseId} from "../cdx-models/Release";
import type {VisionBoardId} from "../cdx-models/VisionBoard";
import type {VisionBoardQueryId} from "../cdx-models/VisionBoardQuery";
import type {ProjectId} from "../cdx-models/Project";
import type {AccountId} from "../cdx-models/Account";
import type {ExitOption} from "../cdx-models/utils/extended-fields";
import type {TemplateMeta} from "../features/templates/template-types";
import type {Promo} from "../features/promo/promo-utils";
import type {
  DiscordChannel,
  DiscordMember,
} from "../pages/account-settings/integrations/discord/discord-types";
import type {
  JiraImportPreviewResult,
  JiraImportResult,
  JiraProjectListItem,
  JiraProjectOverview,
  JiraSlimIssue,
} from "../pages/account-settings/integrations/jira/types";
import type {BillingInterval, PaidPlanType} from "../features/billing/config/billing-utils";
import type {NextInvoicePreview, RecurringInvoicePreview} from "../features/billing/types";
import {API_ERRORS, getDefaultHeaders, smartFetch} from "./request-utils";
import messenger from "./messenger";

type OAuthUrlInput = {targetUrl: string; origin: string; intent?: string};
type OAuthUrlOutput = {link: string; authUrl?: string};

type PostEndpoints = {
  "/integrations/discord/create-login-oauth-url": {input: OAuthUrlInput; output: OAuthUrlOutput};
  "/integrations/discord/create-oauth-url": {input: OAuthUrlInput; output: OAuthUrlOutput};
  "/integrations/discord/create-steamy-oauth-url": {input: OAuthUrlInput; output: OAuthUrlOutput};
  "/integrations/google/create-oauth-url": {input: OAuthUrlInput; output: OAuthUrlOutput};
  "/integrations/jira/create-oauth-url": {input: OAuthUrlInput; output: OAuthUrlOutput};
  "/integrations/jira/site/:siteId/sync/:syncId/import-preview": {
    input: {jql: string};
    output: JiraImportPreviewResult;
  };
  "/integrations/jira/site/:siteId/sync/:syncId/import": {
    input: {jql: string};
    output: JiraImportResult;
  };
  "/integrations/github/create-oauth-url": {input: OAuthUrlInput; output: OAuthUrlOutput};
  "/integrations/slack/create-team-oauth-url": {input: OAuthUrlInput; output: OAuthUrlOutput};
  "/integrations/slack/create-user-oauth-url": {input: OAuthUrlInput; output: OAuthUrlOutput};
  "/services/password-quality": {
    input: {password: string};
    output: {score: number; crackTimeDisplay: string};
  };
  "/billing/create-payment-intent": {
    input: {grossAmountInCents: number; planType?: PaidPlanType; interval?: BillingInterval};
    output: {
      clientSecret: string;
      paymentMethodId: string;
      paymentMethodType: "card" | "sepa_debit";
    };
  };
  "/billing/send-exit-feedback": {
    input: {reason?: string; text?: string};
    output: {ok: true};
  };
  "/billing/create-payment-setup-intent": {
    input: {};
    output: {clientSecret: string};
  };
  "/billing/preview-seat-update": {
    input: {targetSeats: number};
    output: {
      currency: string;
      targetSeats: number;
      currentSeats: number;
      isTrial: boolean;
      trialEndDate: string | null;
      nextInvoice: NextInvoicePreview;
      recurringInvoice: RecurringInvoicePreview;
      nextInvoiceMatchesRecurring: boolean;
    };
  };
  "/billing/preview-plan-upgrade": {
    input: {targetPlanType: "pro"};
    output: {
      currency: string;
      currentSeats: number;
      nextInvoice: NextInvoicePreview;
      recurringInvoice: RecurringInvoicePreview;
      nextInvoiceMatchesRecurring: boolean;
    };
  };
  "/events/visit": {
    input: {
      visitorId: string;
      projectId: string;
      referrer?: string | null;
      utmParams?: {medium?: string | null} | null;
    };
    output: {ok: true};
  };
  "/events/bulk": {
    input: {
      sessionId: string;
      events: {
        type: "button" | "location" | "close";
        data: any;
        msAgo: number;
      }[];
    };
    output: {ok: true};
  };
  "/services/send-release-feedback": {
    input: {
      releaseId: ReleaseId;
      reaction?: string | null;
      content?: string | null;
      rating?: number | null;
    };
    output: {ok: true};
  };
  "/steamy": {
    input: {query: Record<string, any>};
    output: any;
  };
  "/steamy-dispatch/:namespace/:action": {
    input: any;
    output: any;
  };
  "/services/invalidate-all-sessions": {
    input: {};
    output: {ok: true};
  };
  "/services/mark-queries-as-seen": {
    input: {ids: VisionBoardQueryId[]};
    output: {ok: true};
  };
  "": {
    input: {query: Record<string, any>};
    output: any;
  };
  "/dispatch/:namespace/:action": {
    input: any;
    output: any;
  };
  "/services/send-error": {
    input: {message: string; stackFrames?: any[]; appVersion?: string};
    output: {ok: true};
  };
  "/services/clone-projects": {
    input: {
      sourceProjectIds: ProjectId[];
      targetAccountId: AccountId;
      defaultUserAccess: "limited" | "everyone";
    };
    output: {jobId: string};
  };
  "/integrations/:provider/create-webhook-url": {
    input: {};
    output: {link: string};
  };
  "/importer/hacknplan/get-projects": {
    input: {
      authInfo: {apiKey: string};
    };
    output: {id: number; name: string; description: string; closingDate: string};
  };
  "/importer/hacknplan/project-info": {
    input: {
      authInfo: {apiKey: string};
      projectId: number;
    };
    output: {
      metrics: any;
      boards: any;
      importanceLevels: any;
      users: any;
    };
  };
  "/importer/hacknplan/import": {
    input: {
      authInfo: {apiKey: string};
      sourceProjectId: number;
      attachToProjectId?: ProjectId | null;
      projectName?: string | null;
      defaultUserAccess: "limited" | "everyone" | null;
      config: any;
    };
    output: {jobId: string};
  };
  "/importer/trello/get-projects": {
    input: {
      authInfo: {token: string};
    };
    output: {id: string; name: string; closed: boolean; dateLastActivity: string};
  };
  "/importer/trello/project-info": {
    input: {
      authInfo: {token: string};
      projectId: string;
    };
    output: {
      cardCount: number;
      deckCount: number;
      attachmentCount: number;
      attachmentsByteTotal: number;
      membersWithCards: any[];
    };
  };
  "/importer/trello/import": {
    input: {
      authInfo: {token: string};
      sourceProjectId: {id: string};
      attachToProjectId?: ProjectId | null;
      projectName?: string | null;
      defaultUserAccess: "limited" | "everyone" | null;
      config: any;
    };
    output: {jobId: string};
  };
  "/services/generate-cello-token": {
    input: {};
    output: {token: string};
  };
  "/services/vision-board-save": {
    input: {id: string; state: string; version: string};
    output:
      | {ok: true; state: string; version: string}
      | {ok: false; reason: "version_mismatch"; state: string; version: string};
  };
};

type GetEndpoints = {
  "/billing/preview-next-invoice": {
    output:
      | {
          currency: string;
          currentSeats: number;
          isTrial: boolean;
          trialEndDate: string | null;
          nextInvoice: NextInvoicePreview;
          recurringInvoice: RecurringInvoicePreview;
          nextInvoiceMatchesRecurring: boolean;
        }
      | {nextInvoice: null};
  };
  "/billing/on-premise-user-limit": {
    output: {userLimit: number};
  };
  "/services/countries": {
    output: {code: string; name: string}[];
  };
  "/services/deployment-info": {
    output: {createdAt: string; replacedAt: string} | null;
    query: {appName: string; label: string};
  };
  "/services/check-vat-id": {
    output: {valid: boolean};
    query: {vatId: string};
  };
  "/services/random-open-decks": {
    output: {random: any[]};
    query: {skipIds: string};
  };
  "/services/check-email-unique": {
    query: {email: string};
    output: {ok: boolean};
  };
  "/services/check-valid-subdomain": {
    query: {subdomain: string};
    output: {ok: true} | {ok: false; reason: string};
  };
  "/services/vision-board-doc": {
    query: {id: VisionBoardId};
    output: null | {version: string; data: {nodes: any; edges: any}};
  };
  "/services/vision-board-doc-version": {
    query: {id: VisionBoardId};
    output: {version: string};
  };
  "/s3/sign": {
    query: {objectName: string; affectsQuota?: boolean};
    output: {
      signedUrl: string;
      publicUrl: string;
      fields: Record<string, string>;
      sizeLimit: number;
    };
  };
  "/jobs/:id": {
    output:
      | {state: "notFound"; result: null}
      | {state: "error"; result: string}
      | {
          state: "queuing" | "running" | "done" | "error";
          result: any;
          updatedAt: Date;
          createdAt: Date;
        };
  };
  "/integrations/github/repos": {
    output: {
      id: string;
      name: string;
      fullName: string;
      owner: string;
      ownerAvatarUrl: string;
      ownerPage: string;
      repoPage: string;
      description: string;
      isFork: boolean;
      pushedAt: string;
    }[];
  };
  "/services/timezones": {
    output: {
      timezones: {name: string};
    };
  };
  "/services/exit-options": {
    output: Record<"cancelSubscription" | "shutDownOrg", ExitOption[]>;
  };
  "/templates": {
    output: {templates: TemplateMeta[]};
  };
  "/promotion/active": {
    output: null | Promo;
  };
  "/integrations/jira/site/:siteId/projects": {
    output: JiraProjectListItem[];
  };
  "/integrations/jira/site/:siteId/project/:projectId": {
    output: JiraProjectOverview;
  };
  "/integrations/jira/site/:siteId/project/:projectId/issues-by-type/:issueTypeId": {
    output: JiraSlimIssue[];
  };
  "/integrations/discord/get-channels/:id": {
    input: {};
    output: DiscordChannel[];
  };
  "/integrations/discord/get-roles/:id": {
    input: {};
    output: unknown[];
  };
  "/integrations/discord/search-members/:id": {
    query: {query: string};
    output: DiscordMember[];
  };
  "/integrations/discord/get-members/:id": {
    query: {ids: string};
    output: Record<string, DiscordMember | null>;
  };
};

export const postRequest = <T extends keyof PostEndpoints>(
  path: T,
  data: PostEndpoints[T]["input"],
  options?: {signal?: AbortSignal}
): Promise<PostEndpoints[T]["output"]> => {
  return smartFetch<PostEndpoints[T]["output"]>(`${cdxEnv.API_HOST}${path}`, {
    mode: "cors",
    credentials: "include",
    method: "POST",
    body: JSON.stringify(data),
    headers: {
      "Content-Type": "application/json",
      ...getDefaultHeaders(),
    },
    signal: options?.signal,
  });
};

type GetQuery<T extends keyof GetEndpoints> = GetEndpoints[T] extends {query: infer Q}
  ? Q extends Record<string, any>
    ? Q
    : never
  : undefined;

export const getRequest = <T extends keyof GetEndpoints>(
  path: T,
  query?: GetQuery<T>
): Promise<GetEndpoints[T]["output"]> => {
  return smartFetch<GetEndpoints[T]["output"]>(`${cdxEnv.API_HOST}${path}`, {
    mode: "cors",
    credentials: "include",
    method: "GET",
    query,
    headers: {
      ...getDefaultHeaders(),
    },
  });
};

export const useGetRequest = <T extends keyof GetEndpoints>(path: T, query?: GetQuery<T>) => {
  const [state, setState] = useState<
    {type: "loading"} | {type: "success"; data: GetEndpoints[T]["output"]}
  >({type: "loading"});
  const queryAsKey = query ? JSON.stringify(query) : null;
  useEffect(() => {
    let abort: AbortController | null = new AbortController();
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setState({type: "loading"});
    smartFetch<GetEndpoints[T]["output"]>(`${cdxEnv.API_HOST}${path}`, {
      mode: "cors",
      credentials: "include",
      method: "GET",
      query: queryAsKey ? JSON.parse(queryAsKey) : undefined,
      headers: {
        ...getDefaultHeaders(),
      },
      signal: abort.signal,
    }).then(
      (data) => {
        setState({type: "success", data});
        abort = null;
      },
      (error) => {
        if (error instanceof Error && error.name === "AbortError") return;
        messenger.send(`Something went wrong: ${errorToString(error)}`, {type: "error"});
        abort = null;
      }
    );
    return () => {
      if (abort) abort.abort();
    };
  }, [path, queryAsKey]);
  return {isLoading: state.type === "loading", data: state.type === "success" ? state.data : null};
};

type CdxRequestParams = {
  path: string;
  method?: "GET" | "POST";
  data?: any;
  query?: Record<string, any>;
};
const cdxLegacyRequest = <T>({
  path,
  method = "POST",
  data,
  query,
}: CdxRequestParams): Promise<T> => {
  const url = `${cdxEnv.API_HOST}${path}`;
  return smartFetch<T>(url, {
    mode: "cors",
    credentials: "include",
    query,
    method: method,
    body: data ? JSON.stringify(data) : undefined,
    headers: {
      ...(data && {"Content-Type": "application/json"}),
      ...getDefaultHeaders(),
    },
  }).catch((error) => Promise.reject({type: API_ERRORS.FETCH_ERROR, error}));
};

type ServerDataParams = {
  method?: "GET" | "POST";
  path: string | null;
  dataKey?: any;
  data?: any;
  onError?: (error: any) => void;
  onSuccess?: (data: any) => void;
};
export const useServerData = <T>({
  method = "GET",
  path,
  dataKey,
  data,
  onError,
  onSuccess,
}: ServerDataParams) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [result, setResult] = useState<null | T>(null);
  const currentRef = useRef<null | Record<string, never>>(null);
  const refs = useRef({onError, onSuccess, data});
  useEffect(() => {
    refs.current = {onError, onSuccess, data};
  });

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setResult(null);
    if (path === null) {
      setIsLoaded(true);
    } else {
      setIsLoaded(false);
      if (refs.current.onError) {
        refs.current.onError(null);
      }
      const marker = {};
      currentRef.current = marker;
      cdxLegacyRequest({path, method, data: refs.current.data}).then(
        (body) => {
          if (currentRef.current !== marker) return;
          setResult(body as T);
          setIsLoaded(true);
          if (refs.current.onSuccess) refs.current.onSuccess(body);
        },
        (err) => {
          if (currentRef.current !== marker) return;
          if (refs.current.onError) {
            refs.current.onError(errorToString(err));
          } else {
            messenger.send(`Something went wrong: ${errorToString(err)}`, {type: "error"});
          }
          setResult(null);
          setIsLoaded(true);
        }
      );
    }
  }, [method, path, dataKey]);
  return {isLoaded, data: result};
};

const fetchMachine: MachineDescription = {
  ready: {
    initial: true,
    initialData: {},
    on: {
      REQUEST: (fn: () => Promise<unknown>) => ["loading", {promise: fn()}],
    },
  },
  loading: {
    on: {
      SUCCESS: (result: unknown) => ["ready", {lastResult: result}],
      ERROR: (error: unknown) => ["ready", {lastError: error}],
    },
  },
};

export const useServerRequest = ({path}: {path: string}) => {
  const [current, send] = useMachine(fetchMachine);
  const currentRef = useRef(current);
  useEffect(() => {
    currentRef.current = current;
  }, [current]);
  const callFn = useCallback(
    (data: any) => {
      if (currentRef.current.value === "loading") return currentRef.current.data.promise;
      return new Promise((res, rej) => {
        send("REQUEST", () =>
          cdxLegacyRequest({path, method: "POST", data}).then(
            (body) => {
              send("SUCCESS", body);
              res(body);
            },
            (error) => {
              const asString = errorToString(error);
              send("ERROR", asString);
              rej(asString);
            }
          )
        );
      });
    },
    [path, send]
  );
  return [
    callFn,
    {
      isLoading: current.value === "loading",
      error: (current.value === "ready" && current.data.lastError) || null,
      data: (current.value === "ready" && current.data.lastResult) || null,
    },
  ];
};
